import type { CSSProperties } from 'react';

/**
 * Site Inspection Record (SI) chrome tokens -- the single source of truth for
 * the type sizes, colours and spacing of the SI header block.
 *
 * DEVELOPMENT.md, "WYSIWYG Builders: Editor / PDF Parity", rule 1: one module
 * owns the measurements, the stylesheet consumes it as CSS custom properties,
 * and the PDF converts the same numbers to points. Neither surface keeps its
 * own copy. Documents does not yet comply with this for page geometry (those
 * numbers are still triplicated across document-editor.scss, DocumentPDF and
 * DocumentEditor); this module is the compliant half, and the SI chrome is
 * deliberately the first thing built the right way.
 *
 * Everything here is in POINTS, because that is react-pdf's unit and CSS
 * understands `pt` natively -- so the same number is literally written into
 * both surfaces with no conversion to get wrong.
 */

export const SI_TOKENS = {
  /** "SITE INSPECTION RECORD" */
  titleSizePt: 20,
  titleLetterSpacingPt: 0.4,
  /** "PROJECT", "SI NO", ... */
  fieldLabelSizePt: 6.5,
  fieldLabelLetterSpacingPt: 0.9,
  /** The value written on the rule. */
  fieldValueSizePt: 10,
  /** The contract-conditions notice. */
  noticeSizePt: 9,
  /** "REF" / "DESCRIPTION" */
  tableHeadSizePt: 8,
  tableHeadLetterSpacingPt: 1,

  /** Width of the REF column, shared with the instruction rows. */
  refColumnWidthPt: 62,

  // --- vertical rhythm, all in points ---
  //
  // Gap from the title to the field grid. The title used to sit over an
  // "INSPECTION RECORD" subtitle (4pt above it, 18pt below); when the document
  // was renamed Site Inspection Record the subtitle became redundant and was
  // dropped, and the title took over the gap below it.
  titleGapPt: 18,
  fieldRowGapPt: 16,
  fieldLabelGapPt: 5,
  fieldValuePadPt: 3,
  fieldsGapPt: 20,
  noticePadPt: 12,
  noticeLineGapPt: 8,
  noticeGapPt: 18,
  tableHeadPadPt: 6,

  // --- instruction rows ---
  //
  // 0.75pt, not 0.5pt, and the number matters: Chrome resolves a sub-pixel
  // border width to 0.5px (0.375pt) while react-pdf honours the value it is
  // given, so a 0.5pt rule made every row 0.13pt SHORTER in the editor than the
  // PDF drew it. The editor is what paginates, so it was under-measuring --
  // the direction that eventually overflows the PDF's fixed-height page box.
  // 0.75pt is exactly 1px, which both surfaces render identically.
  rowRuleWidthPt: 0.75,
  rowRule: '#c9ccd6',

  // --- distribution list ---
  distributionBoxPt: 11,
  distributionBoxBorderPt: 0.75,
  // The tick is a drawn stroke, not a "✓" character. Tinos has no U+2713, so
  // react-pdf printed every ticked box EMPTY -- no error, just a blank box --
  // while the browser borrowed the glyph from a system font and showed it.
  // One path for both surfaces, in the box's inner square (the box less its
  // border on each side), so the tick is the same shape and weight in each.
  distributionTickPath: 'M2.1 4.9 L3.95 6.8 L7.5 2.7',
  distributionTickStrokePt: 1.1,
  distributionRowGapPt: 5,
  distributionLabelGapPt: 24,
  distributionHeadingGapPt: 8,

  /** Gap between the two grid columns. */
  fieldColumnGapPt: 34,
  /** Gap between PROJECT NO and SI NO, which share the top-right cell. */
  fieldPairGapPt: 14,
  /** The right-hand column (PROJECT NO / SI NO / DATE) is the narrow one. */
  rightColumnWidthPt: 150,
  /**
   * Width of the signature column in the footer row, where the distribution
   * list sits beside it. Fixed rather than "whatever is left over" so the
   * rule and the name keep the length they have on a letter.
   */
  signatureWidthPt: 200,

  // --- colours. Sampled from the printed form. ---
  ink: '#1b1f4b',
  labelInk: '#8a8f9e',
  rule: '#1b1f4b',
  noticeBg: '#f4efe7',
  tableHeadBg: '#1b2333',
  tableHeadInk: '#ffffff',
  accent: '#a9c93c',
} as const;

/**
 * The square a distribution tick is drawn in: the box less its border on each
 * side. distributionTickPath's coordinates are in this space, on both surfaces.
 */
export const distributionTickInnerPt =
  SI_TOKENS.distributionBoxPt - 2 * SI_TOKENS.distributionBoxBorderPt;

/**
 * The tokens as CSS custom properties, applied inline to the SI header element
 * so document-chrome.scss can consume them without a second copy of the values.
 */
export const SI_CSS_VARS = {
  '--si-title-size': `${SI_TOKENS.titleSizePt}pt`,
  '--si-title-tracking': `${SI_TOKENS.titleLetterSpacingPt}pt`,
  '--si-label-size': `${SI_TOKENS.fieldLabelSizePt}pt`,
  '--si-label-tracking': `${SI_TOKENS.fieldLabelLetterSpacingPt}pt`,
  '--si-value-size': `${SI_TOKENS.fieldValueSizePt}pt`,
  '--si-notice-size': `${SI_TOKENS.noticeSizePt}pt`,
  '--si-thead-size': `${SI_TOKENS.tableHeadSizePt}pt`,
  '--si-thead-tracking': `${SI_TOKENS.tableHeadLetterSpacingPt}pt`,
  '--si-ref-width': `${SI_TOKENS.refColumnWidthPt}pt`,
  '--si-title-gap': `${SI_TOKENS.titleGapPt}pt`,
  '--si-field-row-gap': `${SI_TOKENS.fieldRowGapPt}pt`,
  '--si-field-label-gap': `${SI_TOKENS.fieldLabelGapPt}pt`,
  '--si-field-value-pad': `${SI_TOKENS.fieldValuePadPt}pt`,
  '--si-fields-gap': `${SI_TOKENS.fieldsGapPt}pt`,
  '--si-notice-pad': `${SI_TOKENS.noticePadPt}pt`,
  '--si-notice-line-gap': `${SI_TOKENS.noticeLineGapPt}pt`,
  '--si-notice-gap': `${SI_TOKENS.noticeGapPt}pt`,
  '--si-thead-pad': `${SI_TOKENS.tableHeadPadPt}pt`,
  '--si-column-gap': `${SI_TOKENS.fieldColumnGapPt}pt`,
  '--si-pair-gap': `${SI_TOKENS.fieldPairGapPt}pt`,
  '--si-right-column': `${SI_TOKENS.rightColumnWidthPt}pt`,
  '--si-signature-width': `${SI_TOKENS.signatureWidthPt}pt`,
  '--si-ink': SI_TOKENS.ink,
  '--si-label-ink': SI_TOKENS.labelInk,
  '--si-rule': SI_TOKENS.rule,
  '--si-notice-bg': SI_TOKENS.noticeBg,
  '--si-thead-bg': SI_TOKENS.tableHeadBg,
  '--si-thead-ink': SI_TOKENS.tableHeadInk,
  '--si-accent': SI_TOKENS.accent,
  '--si-dist-box': `${SI_TOKENS.distributionBoxPt}pt`,
  '--si-dist-box-border': `${SI_TOKENS.distributionBoxBorderPt}pt`,
  '--si-dist-row-gap': `${SI_TOKENS.distributionRowGapPt}pt`,
  '--si-dist-label-gap': `${SI_TOKENS.distributionLabelGapPt}pt`,
  '--si-dist-heading-gap': `${SI_TOKENS.distributionHeadingGapPt}pt`,
  '--si-row-pad': `${SI_TOKENS.tableHeadPadPt}pt`,
  '--si-row-rule-width': `${SI_TOKENS.rowRuleWidthPt}pt`,
  '--si-row-rule': SI_TOKENS.rowRule,
} as unknown as CSSProperties;
